import { Component, computed, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideIcons } from '@ng-icons/core';
import { lucidePlus } from '@ng-icons/lucide';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { ButtonAddNewComponent } from '../../../shared/components/button-add-new/button-add-new.component';
import { DialogConfirmDeleteComponent } from '../../../shared/components/dialog/confirm-delete/dialog-confirm-delete.component';
import { ConfirmDialogComponent } from '../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import {
  ExpansionPanel,
  ExpansionPanelMenuAction,
} from '../../../shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../shared/components/toastr/toastr-success.component';
import { TrierParPipe } from '../../../shared/pipes/trier-par.pipe';
import { FormFamilleLivrablesComponent } from './form-famille-livrables/form-famille-livrables.component';
import { FormFamillesLivrablesComponent } from './form-familles-livrables/form-familles-livrables.component';
import { FormLivrableComponent } from './form-livrable/form-livrable.component';
import { FormLivrablesComponent } from './form-livrables/form-livrables.component';
import { FamilleLivrables } from './models/famille-livrables.model';
import { Livrable } from './models/livrable.model';
import { FamilleLivrablesStore } from './store/famille-livrables.store';
import { LivrableStore } from './store/livrable.store';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-perimetre-metier',
  imports: [
    HlmIconImports,
    HlmButtonImports,
    HlmItemImports,
    HlmSeparatorImports,
    ExpansionPanel,
    ButtonAddNewComponent,
    TrierParPipe,
    HlmDropdownMenuImports,
    EmptyStateComponent,
  ],
  templateUrl: './perimetre-metier.component.html',
  providers: [provideIcons({ lucidePlus })],
})
export class PerimetreMetierComponent {
  readonly #sidepanelService = inject(SidepanelService);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #familleStore = inject(FamilleLivrablesStore);
  readonly #livrableStore = inject(LivrableStore);

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly famillesLivrables = input.required<FamilleLivrables[]>();
  readonly peutModifier = input.required<boolean>();

  readonly traductions = computed(() => this.referentiel().metadonnees.traductions);

  readonly familleLivrablesAction: ExpansionPanelMenuAction<FamilleLivrables>[] = [
    {
      icon: 'lucidePlus',
      label: 'Ajouter un livrable',
      action: (famille: FamilleLivrables | undefined) => this.ajouterLivrable(famille),
    },
    {
      icon: 'lucidePen',
      label: 'Modifier le nom',
      action: (famille: FamilleLivrables | undefined) => this.#modifierNomFamilleLivrables(famille),
    },
    {
      icon: 'lucideTrash',
      label: 'Supprimer la famille de livrables',
      action: (famille: FamilleLivrables | undefined) => this.#supprimerFamille(famille),
    },
  ];

  readonly livrablesAction: ExpansionPanelMenuAction<Livrable>[] = [
    {
      icon: 'lucidePen',
      label: 'Modifier le livrable',
      action: (livrable: Livrable | undefined) => this.#modifierLivrable(livrable),
    },
    {
      icon: 'lucideTrash',
      label: 'Supprimer le livrable',
      action: (livrable: Livrable | undefined) => this.#supprimerLivrable(livrable),
    },
  ];

  #modifierLivrable(livrable?: Livrable) {
    if (!livrable) return;

    //contraint de faire ça pour récupérer la bonne version du livrable
    livrable = this.famillesLivrables()
      .find((x) => x.id === livrable?.idFamilleLivrables)
      ?.livrables?.find((x) => x.id === livrable?.id);

    this.#sidepanelService.setComponentToDisplay(
      'Éditer un livrable',
      FormLivrableComponent,
      `Pour modifier la ${this.traductions().familleDeLivrables.toLowerCase()} et/ou le nom du ${this.traductions().livrable.toLowerCase()}`,
      {
        livrable,
        referentiel: this.referentiel(),
      },
    );
    this.#sidepanelService.open();
  }

  #modifierNomFamilleLivrables(famille?: FamilleLivrables) {
    if (!famille) return;
    this.#sidepanelService.setComponentToDisplay(
      'Éditer une famille de livrables',
      FormFamilleLivrablesComponent,
      `Pour modifier le nom de la ${this.traductions().familleDeLivrables.toLowerCase()}`,
      { famille, referentiel: this.referentiel() },
    );
    this.#sidepanelService.open();
  }

  ajouterLivrable(famille?: FamilleLivrables) {
    if (!famille) return;
    this.#sidepanelService.setComponentToDisplay(
      'Ajouter un nouveau livrable',
      FormLivrablesComponent,
      `La ${this.traductions().familleDeLivrables.toLowerCase()} est ${famille.nom}.`,
      {
        famille,
      },
    );
    this.#sidepanelService.open();
  }

  showFormFamillesLivrables() {
    this.#sidepanelService.setComponentToDisplay(
      `Ajouter une nouvelle ${this.traductions().familleDeLivrables.toLowerCase()}`,
      FormFamillesLivrablesComponent,
      `Veuillez nommer la ${this.traductions().familleDeLivrables.toLowerCase()} que vous souhaitez créer.`,
      { referentiel: this.referentiel(), cartographie: this.cartographie() },
    );
    this.#sidepanelService.open();
  }

  #supprimerFamille(famille?: FamilleLivrables) {
    if (!famille) return;

    const nbLivrables = famille.livrables?.length ?? 0;
    const toastrId = uuidv7();

    let dialogRef;

    if (nbLivrables) {
      dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
        context: {
          nom: famille.nom,
          childrenText: `${nbLivrables} ${this.traductions().livrable}s liés`,
          typeElement: `la ${this.traductions().familleDeLivrables.toLocaleLowerCase()}`,
        },
      });
    } else {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: `Supprimer la ${this.traductions().familleDeLivrables}`,
          contenu: `Êtes-vous sûr de vouloir supprimer la ${this.traductions().familleDeLivrables} ${famille.nom} ?`,
          confirmBtnText: `Confirmer la suppression`,
        },
      });
    }

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#familleStore.supprimer({
          idFamilleLivrables: famille.id,
          next: () => {
            this.#familleStore.charger({
              idCartographie: this.cartographie().id,
              idRéférentiel: this.referentiel().id,
            });
            toast.custom(ToastrSuccessComponent, {
              componentProps: {
                message: 'Suppression de la famille réussie',
                id: toastrId,
              },
              id: toastrId,
            });
          },
        });
      }
    });
  }

  #supprimerLivrable(livrable?: Livrable) {
    if (!livrable) return;

    const toastrId = uuidv7();

    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: `Supprimer le ${this.traductions().livrable}`,
        contenu: `Êtes-vous sûr de vouloir supprimer le ${this.traductions().livrable} ${livrable.nom} ?`,
        confirmBtnText: `Confirmer la suppression`,
      },
    });

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#livrableStore.supprimer({
          idLivrable: livrable.id,
          next: () => {
            this.#familleStore.charger({
              idCartographie: this.cartographie().id,
              idRéférentiel: this.referentiel().id,
            });
            toast.custom(ToastrSuccessComponent, {
              componentProps: {
                message: 'Suppression du livrable réussie',
                id: toastrId,
              },
              id: toastrId,
            });
          },
        });
      }
    });
  }
}
